/**
 * Login.jsx
 * 
 * Authentication component for the Weekly Bills Planner application.
 * Handles user login, registration, and password reset using Firebase Authentication.
 * 
 * Features:
 * - Email/Password authentication
 * - Google Sign-in
 * - Password reset functionality
 * - Registration for new users
 * - Error handling and validation
 * 
 * @component
 * @requires React
 * @requires Firebase
 */

import React, { useState } from 'react';
import { auth, googleProvider } from '../firebase/config';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail 
} from 'firebase/auth';
import '../styles/Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setError(error.message);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      alert('Password reset email sent! Check your inbox.');
      setShowResetPassword(false);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Weekly Bills Planner</h1>
        <h2>{isRegistering ? 'Create Account' : 'Login'}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        {!showResetPassword ? (
          <form onSubmit={isRegistering ? handleRegister : handleEmailLogin}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="primary-button">
              {isRegistering ? 'Register' : 'Login'}
            </button>
          </form>
        ) : (
          <form onSubmit={handlePasswordReset}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="primary-button">
              Send Reset Link
            </button>
          </form>
        )}

        <button 
          onClick={handleGoogleLogin} 
          className="google-button"
        >
          Sign in with Google
        </button>

        <div className="auth-links">
          {!showResetPassword && (
            <button
              onClick={() => setIsRegistering(!isRegistering)}
              className="link-button"
            >
              {isRegistering 
                ? 'Already have an account? Login' 
                : 'Need an account? Register'}
            </button>
          )}
          <button
            onClick={() => setShowResetPassword(!showResetPassword)}
            className="link-button"
          >
            {showResetPassword 
              ? 'Back to login' 
              : 'Forgot Password?'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login; 